/**
 * Both are top-level ToggleableSectionRepeater schema fields, so the generic schema-walk
 * utilities that drive the insert menu, mention lookup and validation all treat them as
 * repeaters rather than a single mappable leaf field. Anywhere that needs to recognise these
 * two datapoints specially (insert menu grouping, mention rendering, the mention-click popover)
 * shares this list rather than re-deriving it.
 */
export const STRUCTURED_SCHEDULE_DATAPOINT_IDS = ['risk_details.limits_schedule', 'risk_details.deductibles_schedule'];

/** Opening tag of a rendered schedule — how a caller tells real content from the empty label. */
export const SCHEDULE_ITEM_MARKUP_PREFIX = '<span class="schedule-mention-item">';

/**
 * A schedule mention addresses one section's entries — `risk_details.limits_schedule.1.entries` —
 * so its id is already the path to the items it renders. Returns the owning datapoint, which the
 * empty label is derived from, or null when the id is not a schedule.
 */
export const parseScheduleDatapointId = (id: string): { datapointId: string } | null => {
  const sectionSuffix = /\.\d+\.entries$/.exec(id);
  if (!sectionSuffix) return null;

  const datapointId = id.slice(0, -sectionSuffix[0].length);

  return STRUCTURED_SCHEDULE_DATAPOINT_IDS.includes(datapointId) ? { datapointId } : null;
};

export const isScheduleDatapointId = (id: string): boolean => parseScheduleDatapointId(id) !== null;

/**
 * Shown in place of an inserted-but-unpopulated schedule, so the block reads as a sentence rather
 * than leaving the raw `{{limits_schedule}}` token on the page. Mirrors the derived-content
 * wording ("No conditions specified", see helpers/clauses/utils.ts) and is derived from the
 * datapoint id so a third schedule needs no new copy.
 */
export const getScheduleEmptyLabel = (datapointId: string): string =>
  `No ${(datapointId.split('.').pop() ?? 'schedule').replaceAll('_', ' ')} specified`;
